import { Capacitor } from "@capacitor/core";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useAdonisChat } from "@/modules/adonis/AdonisChatProvider/AdonisChatProvider";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { NotificationCard } from "@/modules/onboarding/components/NotificationCard/NotificationCard";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import { useUser } from "@/modules/user";
import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";
import { usePlatform } from "hooks/usePlatform";
import { AndroidPushNotificationAdapter } from "modules/pushNotifications/AndroidPushNotificationAdapter/AndroidPushNotificationAdapter";
import { IOSPushNotificationAdapter } from "modules/pushNotifications/IOSPushNotificationAdapter/IOSPushNotificationAdapter";
import { PushNotificationService } from "modules/pushNotifications/PushNotificationService/PushNotificationService";
import { isIosPlatform } from "utils/platform.helpers";

export default function V3NotificationsPage() {
  const navigate = useNavigate();
  const [isRequesting, setIsRequesting] = useState(false);

  const { platform } = usePlatform();
  const { user } = useUser();
  const firstName = user?.firstName || "";

  const { isPersonalisationEnabled } = useAdonisChat();

  const handleNext = useCallback(() => {
    navigate(isPersonalisationEnabled ? "/personalise" : "/home");
  }, [navigate, isPersonalisationEnabled]);

  const handleEnableNotifications = async () => {
    if (!Capacitor.isNativePlatform()) {
      handleNext();
      return;
    }

    setIsRequesting(true);
    try {
      const adapter = isIosPlatform()
        ? new IOSPushNotificationAdapter()
        : new AndroidPushNotificationAdapter();
      const pushService = new PushNotificationService(adapter);
      const permissionsGranted = await pushService.requestPermission();

      if (permissionsGranted) {
        await pushService.register();
      }
    } catch (error) {
      console.error("Failed to request notification permissions:", error);
    } finally {
      setIsRequesting(false);
      handleNext();
    }
  };

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      handleNext();
    }
  }, [handleNext]);

  return (
    <MobileViewportContainer header={<OnboardingBackButton />}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        className="flex w-full max-w-sm flex-col items-center gap-9"
      >
        {/* Header - 36px gap after */}
        <div className="flex w-full shrink-0 flex-col gap-4">
          <p className="w-full shrink-0 font-serif text-[30px] font-normal italic leading-[36px] text-nearly-black">
            To get the VIP experience, you&apos;ll need notifications.
          </p>
          <p
            className="w-full shrink-0 font-sans text-body2 text-secondary"
            style={{ fontVariationSettings: "'opsz' 14" }}
          >
            {`Don't miss out on flash promos, limited time offers, and exclusive
            rewards from ${platform?.name}.`}
          </p>
        </div>

        {/* Notification Card - 36px gap after */}
        <div className="w-full shrink-0">
          <NotificationCard
            spaName={platform?.name || ""}
            timeAgo="10 min ago"
            avatar={platform?.mobileAppIconUrlSmall || ""}
            title={`Way to go, ${firstName} 🎉`}
            message="You've unlocked a new reward - Tap now to check it out!"
            className="!max-w-full bg-white/40 shadow-[0px_3px_4px_0px_rgba(0,0,0,0.1)]"
          />
        </div>

        {/* Buttons - 16px gap between them */}
        <div className="flex w-full shrink-0 flex-col items-center gap-4">
          <Button
            fullWidth
            onClick={handleEnableNotifications}
            disabled={isRequesting}
          >
            {isRequesting ? "Requesting..." : "Enable notifications"}
          </Button>
          <TextButton className="px-3 font-normal" onClick={handleNext}>
            {`I'll do it later`}
          </TextButton>
        </div>
      </motion.div>
    </MobileViewportContainer>
  );
}
